import { supabase } from '@/lib/supabaseClient';

// Types
export type KnowledgeSourceType = 'app_faq' | 'sales_training' | 'custom_docs' | 'crm_data';

export interface KnowledgeSource {
  id: string;
  name: string;
  source_type: KnowledgeSourceType;
  description?: string;
  configuration?: Record<string, any>;
  query_weight: number;
  is_enabled: boolean;
  requires_citation: boolean;
  tenant_specific: boolean;
}

export interface QueryResult {
  document_id: string;
  source_type: KnowledgeSourceType;
  document_title: string;
  chunk_text: string;
  similarity: number;
  metadata?: Record<string, any>;
  citation?: string;
}

export interface SourceConfiguration {
  source_type: KnowledgeSourceType;
  enabled: boolean;
  weight: number;
  max_results?: number;
}

class KnowledgeSourceManager {
  private sources: Map<KnowledgeSourceType, KnowledgeSource> = new Map();

  async initialize() {
    const { data, error } = await supabase
      .from('knowledge_sources')
      .select('*');
    
    if (error) {
      console.error('Error loading knowledge sources:', error);
      return;
    }

    data?.forEach(source => {
      this.sources.set(source.source_type, source);
    });
  }

  /**
   * Determine which knowledge sources to query based on the user's question
   */
  routeQuery(query: string): KnowledgeSourceType[] {
    const sources: KnowledgeSourceType[] = [];
    const lowerQuery = query.toLowerCase();

    // Always include app FAQs for general questions
    sources.push('app_faq');

    // Check for sales-related keywords
    if (this.containsSalesKeywords(lowerQuery)) {
      sources.push('sales_training');
    }

    // Check for document-related keywords
    if (this.containsDocumentKeywords(lowerQuery)) {
      sources.push('custom_docs');
    }

    // Check for CRM/project keywords
    if (this.containsCRMKeywords(lowerQuery)) {
      sources.push('crm_data');
    }

    // If no specific matches, search all enabled sources
    if (sources.length === 1) {
      const enabledSources = Array.from(this.sources.values())
        .filter(s => s.is_enabled)
        .map(s => s.source_type);
      return enabledSources;
    }

    return sources;
  }

  /**
   * Perform confidence-based cascade search
   * Searches sources in priority order based on confidence thresholds
   */
  async cascadeSearch(
    queryEmbedding: number[],
    query: string,
    tenantId: string,
    maxResults: number = 10
  ): Promise<{
    results: QueryResult[];
    confidence: number;
    sourcesSearched: KnowledgeSourceType[];
  }> {
    const sourcesSearched: KnowledgeSourceType[] = [];
    let allResults: QueryResult[] = [];
    let confidence = 0;

    // Priority 1: Always search app_faq first for general questions
    if (this.sources.get('app_faq')?.is_enabled) {
      const faqResults = await this.queryMultipleSources(
        queryEmbedding,
        tenantId,
        ['app_faq'],
        0.5, // Lower threshold for FAQ to catch more potential matches
        maxResults
      );
      
      if (faqResults.length > 0) {
        allResults = [...faqResults];
        confidence = Math.max(...faqResults.map(r => r.similarity));
        sourcesSearched.push('app_faq');
      }

      // If FAQ confidence >= 0.8, return immediately (high confidence match)
      if (confidence >= 0.8) {
        return { results: allResults, confidence, sourcesSearched };
      }
    }

    // Priority 2: Add sales_training if confidence < 0.8
    if (confidence < 0.8 && this.sources.get('sales_training')?.is_enabled) {
      const salesResults = await this.queryMultipleSources(
        queryEmbedding,
        tenantId,
        ['sales_training'],
        0.5,
        maxResults
      );
      
      if (salesResults.length > 0) {
        allResults = this.mergeResults([...allResults, ...salesResults]);
        confidence = Math.max(confidence, ...salesResults.map(r => r.similarity));
        sourcesSearched.push('sales_training');
      }

      // If combined confidence >= 0.7, return
      if (confidence >= 0.7) {
        return { results: allResults.slice(0, maxResults), confidence, sourcesSearched };
      }
    }

    // Priority 3: Add custom_docs if confidence < 0.7 (tenant-specific only)
    if (confidence < 0.7 && this.sources.get('custom_docs')?.is_enabled) {
      const customResults = await this.queryMultipleSources(
        queryEmbedding,
        tenantId,
        ['custom_docs'],
        0.5,
        maxResults
      );
      
      if (customResults.length > 0) {
        allResults = this.mergeResults([...allResults, ...customResults]);
        confidence = Math.max(confidence, ...customResults.map(r => r.similarity));
        sourcesSearched.push('custom_docs');
      }
    }

    // Priority 4: Only search CRM if explicitly requested via keywords
    const lowerQuery = query.toLowerCase();
    if (this.containsCRMKeywords(lowerQuery) && this.sources.get('crm_data')?.is_enabled) {
      // CRM data is handled separately in aiChatService
      // Just mark it as searched for tracking
      sourcesSearched.push('crm_data');
    }

    return { 
      results: allResults.slice(0, maxResults), 
      confidence,
      sourcesSearched 
    };
  }

  /**
   * Query multiple knowledge sources in parallel
   */
  async queryMultipleSources(
    queryEmbedding: number[],
    tenantId: string,
    sourceTypes?: KnowledgeSourceType[],
    threshold: number = 0.7,
    maxResults: number = 10
  ): Promise<QueryResult[]> {
    try {
      // Call the database function to search knowledge
      const { data, error } = await supabase.rpc('search_multi_source_knowledge', {
        query_embedding: queryEmbedding,
        p_tenant_id: tenantId,
        source_types: sourceTypes || null,
        match_threshold: threshold,
        match_count: maxResults
      });

      if (error) {
        console.error('Error searching knowledge base:', error);
        return [];
      }

      // Process results and add citations if needed
      const results: QueryResult[] = (data || []).map((item: any) => {
        const result: QueryResult = {
          document_id: item.document_id,
          source_type: item.source_type,
          document_title: item.document_title,
          chunk_text: item.chunk_text,
          similarity: item.similarity,
          metadata: item.metadata
        };

        // Add citation for sales training content
        if (item.source_type === 'sales_training' && this.sources.get('sales_training')?.requires_citation) {
          result.citation = this.formatCitation(item);
        }

        return result;
      });

      return results;
    } catch (error) {
      console.error('Error querying knowledge sources:', error);
      return [];
    }
  }

  /**
   * Merge and rank results from multiple sources
   */
  mergeResults(results: QueryResult[], sourceWeights?: Map<KnowledgeSourceType, number>): QueryResult[] {
    // Apply source weights if provided
    if (sourceWeights) {
      results.forEach(result => {
        const weight = sourceWeights.get(result.source_type) || 1.0;
        result.similarity *= weight;
      });
    }

    // Sort by weighted similarity
    results.sort((a, b) => b.similarity - a.similarity);

    // Remove duplicates based on chunk text similarity
    const uniqueResults: QueryResult[] = [];
    const seenTexts = new Set<string>();

    for (const result of results) {
      const textSignature = result.chunk_text.substring(0, 100).toLowerCase();
      if (!seenTexts.has(textSignature)) {
        uniqueResults.push(result);
        seenTexts.add(textSignature);
      }
    }

    return uniqueResults;
  }

  /**
   * Apply citations to results that require them
   */
  applyCitations(results: QueryResult[]): QueryResult[] {
    return results.map(result => {
      if (result.source_type === 'sales_training' && !result.citation) {
        result.citation = this.formatCitation(result);
      }
      return result;
    });
  }

  /**
   * Format citation for a result
   */
  private formatCitation(result: any): string {
    const metadata = result.metadata || {};
    
    switch (result.source_type) {
      case 'sales_training':
        const speaker = metadata.speaker || 'Unknown Speaker';
        const timestamp = metadata.timestamp || '';
        const videoTitle = result.document_title || 'Video';
        return `[${speaker}, ${videoTitle}${timestamp ? `, ${timestamp}` : ''}]`;
      
      case 'custom_docs':
        const page = metadata.page || '';
        const section = metadata.section || '';
        return `[${result.document_title}${page ? `, Page ${page}` : ''}${section ? `, ${section}` : ''}]`;
      
      case 'app_faq':
        const category = metadata.category || 'General';
        return `[FAQ: ${result.document_title}, ${category}]`;
      
      case 'crm_data':
        const recordType = metadata.record_type || 'Record';
        const lastUpdated = metadata.last_updated || '';
        return `[${recordType}: ${result.document_title}${lastUpdated ? `, Updated ${lastUpdated}` : ''}]`;
      
      default:
        return `[${result.document_title}]`;
    }
  }

  /**
   * Check if query contains FAQ-specific keywords (high priority for app_faq)
   */
  private containsFAQKeywords(query: string): boolean {
    const keywords = [
      'how to', 'how do', 'what is', 'what are', 'what states',
      'where can', 'where do', 'help', 'guide', 'tutorial',
      'explain', 'why', 'when should', 'can i', 'can you',
      'service area', 'coverage', 'locations', 'available in',
      'solerro', 'joules', 'company', 'about', 'features',
      'pricing', 'cost', 'plan', 'subscription', 'billing'
    ];
    
    // Check for common FAQ patterns
    const patterns = [
      /^(how|what|where|why|when|can|should|do|is|are)/i,
      /help.*(with|me|about)/i,
      /(service|cover|available).*(state|area|location)/i,
      /what.*(state|location|area)/i
    ];
    
    return keywords.some(keyword => query.includes(keyword)) ||
           patterns.some(pattern => pattern.test(query));
  }

  /**
   * Check if query contains sales-related keywords
   */
  private containsSalesKeywords(query: string): boolean {
    const keywords = [
      'sales', 'sell', 'selling', 'pitch', 'close', 'closing',
      'objection', 'prospect', 'lead', 'negotiation', 'deal',
      'commission', 'quota', 'training video', 'sales technique',
      'overcome', 'handle objection', 'convince', 'persuade'
    ];
    
    // Exclude if it's clearly an FAQ question
    if (this.containsFAQKeywords(query)) {
      return false;
    }
    
    return keywords.some(keyword => query.includes(keyword));
  }

  /**
   * Check if query contains document-related keywords
   */
  private containsDocumentKeywords(query: string): boolean {
    const keywords = [
      'document', 'file', 'pdf', 'policy', 'procedure',
      'guide', 'manual', 'documentation', 'upload', 'attachment',
      'form', 'template', 'checklist', 'process'
    ];
    
    // Exclude if it's clearly an FAQ question
    if (this.containsFAQKeywords(query)) {
      return false;
    }
    
    return keywords.some(keyword => query.includes(keyword));
  }

  /**
   * Check if query contains CRM/project keywords
   */
  private containsCRMKeywords(query: string): boolean {
    // First check if this looks like an FAQ question about projects/states
    if (this.containsFAQKeywords(query)) {
      // Check for specific patterns that should still go to CRM
      const crmPatterns = [
        /my\s+(project|customer|task|appointment)/i,
        /show\s+me\s+(project|customer|task)/i,
        /list\s+(of\s+)?(project|customer|task)/i,
        /(project|customer|task)\s+(status|update|detail)/i,
        /^(get|fetch|find|show|display).*project/i
      ];
      
      if (!crmPatterns.some(pattern => pattern.test(query))) {
        return false; // It's an FAQ about projects, not a CRM query
      }
    }
    
    const keywords = [
      'my project', 'my customer', 'my task', 'my appointment',
      'project status', 'ticket', 'pipeline', 'stage change',
      'task assigned', 'appointment scheduled', 'meeting with',
      'follow-up', 'site visit', 'installation status'
    ];
    
    // More specific multi-word patterns for CRM
    const patterns = [
      /(show|list|get|find).*(project|customer|ticket|task)/i,
      /project.*(status|update|detail|info)/i,
      /(residential|commercial).*(project|customer)/i
    ];
    
    return keywords.some(keyword => query.includes(keyword)) ||
           patterns.some(pattern => pattern.test(query));
  }

  /**
   * Get configuration for a specific source
   */
  getSourceConfig(sourceType: KnowledgeSourceType): KnowledgeSource | undefined {
    return this.sources.get(sourceType);
  }

  /**
   * Update source configuration
   */
  async updateSourceConfig(sourceType: KnowledgeSourceType, updates: Partial<KnowledgeSource>) {
    const { error } = await supabase
      .from('knowledge_sources')
      .update(updates)
      .eq('source_type', sourceType);

    if (error) {
      console.error('Error updating source config:', error);
      throw error;
    }

    // Update local cache
    const currentConfig = this.sources.get(sourceType);
    if (currentConfig) {
      this.sources.set(sourceType, { ...currentConfig, ...updates });
    }
  }
}

// Export singleton instance
export const knowledgeSourceManager = new KnowledgeSourceManager();